(() => {
  const config = window.QamarShippingProtectionConfig || {};
  const rawVariantId = config.variantId;
  const variantId = Number(rawVariantId);
  const variantIdString = rawVariantId ? String(rawVariantId) : '';
  const DISABLED_KEY = config.disabledKey || 'qamar_shipping_protection_disabled';

  if (!variantIdString) {
    console.warn('[ShippingProtection] Missing variant id. Set QamarShippingProtectionConfig.variantId.');
  }

  let isSyncing = false;

  // ── Cached shipping-protection price (cents) ──────────────────────
  let protectionPriceCents = 0;

  const resolveProtectionPrice = (cart) => {
    if (protectionPriceCents) return;

    if (cart && Array.isArray(cart.items)) {
      const item = cart.items.find(i => String(i.variant_id) === variantIdString);
      if (item) {
        protectionPriceCents = item.final_price || item.price || 0;
        return;
      }
    }

    const widget = document.querySelector('[data-protection-price-cents]');
    if (widget) {
      const cents = parseInt(widget.dataset.protectionPriceCents, 10);
      if (cents > 0) protectionPriceCents = cents;
    }
  };

  // ── Money formatter ────────────────────────────────────────────────
  const formatMoney = (cents) => {
    const format = window.theme?.settings?.money_with_currency_format
                || window.theme?.settings?.money_format
                || '${{amount}}';
    const amount      = (cents / 100).toFixed(2);
    const amountNoD   = Math.floor(cents / 100).toString();
    const amountWithC = amount.replace(/(\d)(?=(\d{3})+\.)/g, '$1,');
    const amountNoDC  = amountNoD.replace(/(\d)(?=(\d{3})+$)/g, '$1,');

    return format
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ',').replace(/(\d)(?=(\d{3})+,)/g, '$1.'))
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoD.replace(/(\d)(?=(\d{3})+$)/g, '$1.'))
      .replace('{{amount_with_apostrophe_separator}}', amount.replace(/(\d)(?=(\d{3})+\.)/g, "$1'"))
      .replace('{{amount_no_decimals}}', amountNoDC)
      .replace('{{amount}}', amountWithC);
  };

  // ── Cart counters ──────────────────────────────────────────────────
  const updateCartCounters = (cartState) => {
    const applyCount = (count) => {
      document.querySelectorAll('.cart-count-badge').forEach(badge => {
        if (count > 0) {
          badge.classList.remove('hidden');
          badge.textContent = count > 99 ? '+' : String(count);
        } else {
          badge.classList.add('hidden');
          badge.textContent = '';
        }
      });

      document.querySelectorAll('.cart__title-counter').forEach(counter => {
        counter.textContent = String(count);
      });

      document.querySelectorAll('.cart-drawer__title-counter').forEach(counter => {
        counter.textContent = String(count);
      });
    };

    if (cartState && Array.isArray(cartState.items)) {
      const visibleCount = cartState.items
        .filter(item => String(item.variant_id) !== variantIdString)
        .reduce((sum, item) => sum + item.quantity, 0);
      applyCount(visibleCount);
      return;
    }

    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const visibleCount = cart.items
          .filter(item => String(item.variant_id) !== variantIdString)
          .reduce((sum, item) => sum + item.quantity, 0);
        applyCount(visibleCount);
        syncShippingProtection();
      })
      .catch(() => {});
  };

  // Force override the global updateCartCounters from global.js
  // so that cart updates correctly exclude the protection item.
  window.updateCartCounters = updateCartCounters;

  // ── Donation bar refresh ──────────────────────────────────────────
  // After any shipping-protection change, refresh the donation bar
  // with authoritative cart data (excluding protection variant).
  const refreshDonationBar = (cart) => {
    if (typeof window.updateDonationBar !== 'function') return;
    if (cart && Array.isArray(cart.items)) {
      window.updateDonationBar(cart);
    } else {
      window.updateDonationBar(); // triggers fetchAndUpdate fallback
    }
  };

  // ── Cart helpers & Interceptors ────────────────────────────────────
  // Override fetch globally to hide protection item from third-party apps,
  // and to inject it instantly on Add to Cart to avoid visual delays.
  const originalFetch = window.fetch;
  window.fetch = async function(...args) {
    const url = typeof args[0] === 'string' ? args[0] : (args[0] instanceof Request ? args[0].url : '');
    
    // Inject shipping protection on Add to Cart to prevent 1-second delay
    if (url && (url.includes('/cart/add'))) {
      if (!url.includes('unfiltered=true') && !isDisabled()) {
        try {
          const options = args[1] || {};
          if (options.method && options.method.toUpperCase() === 'POST' && options.body) {
            
            // Handle JSON and URL-encoded string payloads
            if (typeof options.body === 'string') {
              let parsedSuccessfully = false;
              let bodyObj = {};
              
              try {
                bodyObj = JSON.parse(options.body);
                parsedSuccessfully = true;
              } catch (e) {
                // If it's not JSON, it might be a URL-encoded form string (e.g. "id=123&quantity=1")
                if (options.body.includes('id=')) {
                  const params = new URLSearchParams(options.body);
                  const originalId = params.get('id');
                  const hasItems = Array.from(params.keys()).some(k => k.includes('items['));
                  
                  if (!hasItems && originalId && String(originalId) !== variantIdString) {
                    const originalQty = params.get('quantity') || 1;
                    
                    // Rebuild the body as JSON since we need to send an items array
                    bodyObj = {
                      items: [
                        { id: originalId, quantity: originalQty },
                        { id: variantIdString, quantity: 1 }
                      ]
                    };
                    
                    // Copy any extra properties
                    for (const [key, value] of params.entries()) {
                      if (key !== 'id' && key !== 'quantity') {
                        bodyObj[key] = value;
                      }
                    }
                    
                    args[1].body = JSON.stringify(bodyObj);
                    
                    // Fix headers to be JSON
                    if (args[1].headers instanceof Headers) {
                      args[1].headers.set('Content-Type', 'application/json');
                      args[1].headers.set('Accept', 'application/json');
                    } else {
                      args[1].headers = args[1].headers || {};
                      args[1].headers['Content-Type'] = 'application/json';
                      args[1].headers['Accept'] = 'application/json';
                    }
                  }
                }
              }

              // If it WAS parsed as JSON, handle it:
              if (parsedSuccessfully) {
                if (bodyObj.id && !bodyObj.items) {
                  bodyObj.items = [{ id: bodyObj.id, quantity: bodyObj.quantity || 1 }];
                  delete bodyObj.id;
                  delete bodyObj.quantity;
                }
                if (bodyObj.items && Array.isArray(bodyObj.items)) {
                  const hasProtection = bodyObj.items.some(i => String(i.id) === variantIdString);
                  if (!hasProtection) {
                    bodyObj.items.push({ id: variantIdString, quantity: 1 });
                    args[1].body = JSON.stringify(bodyObj);
                  }
                }
              }
            } 
            // Handle FormData payloads
            else if (options.body instanceof FormData) {
              const originalId = options.body.get('id');
              const hasItems = Array.from(options.body.keys()).some(k => k.includes('items['));
              
              if (!hasItems && originalId && String(originalId) !== variantIdString) {
                const originalQty = options.body.get('quantity') || 1;
                
                // Convert single id/quantity to items array format in FormData
                options.body.delete('id');
                options.body.delete('quantity');
                
                options.body.append('items[0][id]', originalId);
                options.body.append('items[0][quantity]', originalQty);
                
                options.body.append('items[1][id]', variantIdString);
                options.body.append('items[1][quantity]', 1);
              }
            }
          }
        } catch (e) {} // Fallback to normal behavior if parsing fails
      }
    }

    const response = await originalFetch.apply(this, args);
    
    if (url && (url.includes('/cart.js') || url.includes('/cart/change') || url.includes('/cart/add'))) {
      if (url.includes('unfiltered=true')) return response;
      
      const clonedResponse = response.clone();
      try {
        const text = await clonedResponse.text();
        if (text) {
          const data = JSON.parse(text);
          if (data && data.items) {
            data.items = data.items.filter(item => String(item.variant_id) !== variantIdString);
            if (typeof data.item_count !== 'undefined') {
              data.item_count = data.items.reduce((total, item) => total + item.quantity, 0);
            }
            return new Response(JSON.stringify(data), {
              status: response.status,
              statusText: response.statusText,
              headers: response.headers
            });
          }
        }
      } catch (e) {} // ignore
    }
    return response;
  };

  const originalXHR = window.XMLHttpRequest;
  window.XMLHttpRequest = function() {
    const xhr = new originalXHR();
    const originalOpen = xhr.open;
    const originalSend = xhr.send;
    
    xhr.open = function(method, url) {
      this._method = method;
      this._url = typeof url === 'string' ? url : '';
      return originalOpen.apply(this, arguments);
    };

    xhr.send = function(body) {
      if (this._url && this._url.includes('/cart/add')) {
        if (!this._url.includes('unfiltered=true') && !isDisabled()) {
          try {
            if (this._method && this._method.toUpperCase() === 'POST' && body) {
              
              // Handle JSON and URL-encoded string payloads
              if (typeof body === 'string') {
                let parsedSuccessfully = false;
                let bodyObj = {};
                
                try {
                  bodyObj = JSON.parse(body);
                  parsedSuccessfully = true;
                } catch (e) {
                  // Fallback for URL-encoded string (id=123&quantity=1)
                  if (body.includes('id=')) {
                    const params = new URLSearchParams(body);
                    const originalId = params.get('id');
                    const hasItems = Array.from(params.keys()).some(k => k.includes('items['));
                    
                    if (!hasItems && originalId && String(originalId) !== variantIdString) {
                      const originalQty = params.get('quantity') || 1;
                      
                      bodyObj = {
                        items: [
                          { id: originalId, quantity: originalQty },
                          { id: variantIdString, quantity: 1 }
                        ]
                      };
                      
                      for (const [key, value] of params.entries()) {
                        if (key !== 'id' && key !== 'quantity') {
                          bodyObj[key] = value;
                        }
                      }
                      
                      arguments[0] = JSON.stringify(bodyObj);
                      xhr.setRequestHeader('Content-Type', 'application/json');
                      xhr.setRequestHeader('Accept', 'application/json');
                    }
                  }
                }

                if (parsedSuccessfully) {
                  if (bodyObj.id && !bodyObj.items) {
                    bodyObj.items = [{ id: bodyObj.id, quantity: bodyObj.quantity || 1 }];
                    delete bodyObj.id;
                    delete bodyObj.quantity;
                  }
                  if (bodyObj.items && Array.isArray(bodyObj.items)) {
                    const hasProtection = bodyObj.items.some(i => String(i.id) === variantIdString);
                    if (!hasProtection) {
                      bodyObj.items.push({ id: variantIdString, quantity: 1 });
                      arguments[0] = JSON.stringify(bodyObj);
                    }
                  }
                }
              } 
              // Handle FormData payloads
              else if (body instanceof FormData) {
                const originalId = body.get('id');
                const hasItems = Array.from(body.keys()).some(k => k.includes('items['));
                
                if (!hasItems && originalId && String(originalId) !== variantIdString) {
                  const originalQty = body.get('quantity') || 1;
                  
                  body.delete('id');
                  body.delete('quantity');
                  
                  body.append('items[0][id]', originalId);
                  body.append('items[0][quantity]', originalQty);
                  
                  body.append('items[1][id]', variantIdString);
                  body.append('items[1][quantity]', 1);
                }
              }
            }
          } catch (e) {} // ignore and proceed with original send
        }
      }
      return originalSend.apply(this, arguments);
    };
    
    // We proxy responseText getter
    const originalResponseText = Object.getOwnPropertyDescriptor(originalXHR.prototype, 'responseText');
    if (originalResponseText) {
      Object.defineProperty(xhr, 'responseText', {
        get: function() {
          const text = originalResponseText.get.call(this);
          if (this._url && (this._url.includes('/cart.js') || this._url.includes('/cart/change') || this._url.includes('/cart/add'))) {
            if (!this._url.includes('unfiltered=true')) {
              try {
                const data = JSON.parse(text);
                if (data && data.items) {
                  data.items = data.items.filter(item => String(item.variant_id) !== variantIdString);
                  if (typeof data.item_count !== 'undefined') {
                    data.item_count = data.items.reduce((total, item) => total + item.quantity, 0);
                  }
                  return JSON.stringify(data);
                }
              } catch (e) {}
            }
          }
          return text;
        }
      });
    }
    return xhr;
  };

  const getCart = () => fetch('/cart.js?unfiltered=true').then(response => response.json());

  const getProtectionItems = (cart) =>
    cart.items.filter(item => String(item.variant_id) === variantIdString);

  const getRegularItems = (cart) =>
    cart.items.filter(item => String(item.variant_id) !== variantIdString);

  const isDisabled = () => sessionStorage.getItem(DISABLED_KEY) === 'true';

  const setDisabled = (value) => {
    if (value) {
      sessionStorage.setItem(DISABLED_KEY, 'true');
    } else {
      sessionStorage.removeItem(DISABLED_KEY);
    }
  };

  const postCart = (url, payload) => {
    const body = JSON.stringify({
      ...payload,
      sections_url: window.location.pathname
    });

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body
    }).then(response => response.json());
  };

  // ── Visual helpers (no API calls) ──────────────────────────────────
  const setWidgetState = (enabled) => {
    const widget = document.querySelector('[data-shipping-protection]');
    if (!widget) return;

    widget.classList.toggle('shipping-protection--disabled', !enabled);

    const toggle = widget.querySelector('[data-shipping-protection-toggle]');
    if (toggle) {
      toggle.checked = enabled;
    }
  };

  const setIconAppearance = (enabled) => {
    // Widget-level icon (summary area)
    const widget = document.querySelector('[data-shipping-protection]');
    if (widget) {
      const img = widget.querySelector('.shipping-protection__icon img');
      if (img) {
        img.style.filter = enabled ? '' : 'grayscale(1)';
        img.style.opacity = enabled ? '' : '0.6';
      }
    }

    // Line-item level icon (if visible)
    document.querySelectorAll('.cart-item--shipping-protection .shipping-protection__icon img').forEach(img => {
      img.style.filter = enabled ? '' : 'grayscale(1)';
      img.style.opacity = enabled ? '' : '0.6';
    });
  };

  const getCartTotalElements = () => Array.from(
    document.querySelectorAll('[data-cart-total-price]')
  );

  const updateSubtotalFromServer = (totalPriceCents) => {
    const priceElements = getCartTotalElements();
    if (!priceElements.length) return;
    priceElements.forEach(priceEl => {
      priceEl.dataset.cartTotalPrice = String(totalPriceCents);
      priceEl.textContent = formatMoney(totalPriceCents);
    });
  };

  const setCheckoutDisabled = (disabled) => {
    const buttons = document.querySelectorAll(
      '.cart-drawer__action-buttons [name="checkout"], .cart__summary-actions [name="checkout"]'
    );

    buttons.forEach(button => {
      button.disabled = disabled;
      if (disabled) {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    });
  };

  // ── Optimistic state tracking ──────────────────────────────────────
  // We track the ABSOLUTE optimistic state so that rapid clicks don't
  // compound price deltas. Each click only adjusts the price if the
  // desired state differs from what the UI is currently showing.
  //   null  = unknown (initial, before first render)
  //   true  = UI shows protection enabled (price includes protection)
  //   false = UI shows protection disabled (price excludes protection)
  let optimisticState = null;

  /**
   * Apply optimistic visuals for the desired protection state.
   * Safe to call repeatedly — only adjusts price when state changes.
   */
  const applyOptimisticUI = (enabled) => {
    setWidgetState(enabled);
    setIconAppearance(enabled);

    // Only adjust price when state actually flips
    if (protectionPriceCents && optimisticState !== null && optimisticState !== enabled) {
      const priceElements = getCartTotalElements();
      priceElements.forEach(priceEl => {
        const currentCents = parseInt(priceEl.dataset.cartTotalPrice, 10) || 0;
        const newCents = Math.max(0, currentCents + (enabled ? protectionPriceCents : -protectionPriceCents));
        priceEl.dataset.cartTotalPrice = String(newCents);
        priceEl.textContent = formatMoney(newCents);
      });
    }

    optimisticState = enabled;
  };

  const initOptimisticState = () => {
    const toggle = document.querySelector('[data-shipping-protection-toggle]');
    if (toggle) {
      optimisticState = toggle.checked;
    }
  };

  // ── Serial toggle queue ────────────────────────────────────────────
  // Instead of a simple debounce, we use a serial queue pattern:
  //   1. Every click stores the desired state and resets a short timer.
  //   2. When the timer fires, we lock processing and execute.
  //   3. While `isUserToggling` is true, ALL syncs are blocked.
  //   4. `isProcessingQueue` prevents concurrent queue runs when a
  //      second timer fires while the first is still in async API calls.
  //   5. The running queue picks up any new pendingDesiredState on loop.
  let toggleDebounceTimer = null;
  let isUserToggling = false;       // true from first click until final API settles
  let isProcessingQueue = false;    // true while processToggleQueue is running (async)
  let pendingDesiredState = null;   // the latest desired state from user clicks

  const handleToggle = (event) => {
    const toggle = event.target.closest('[data-shipping-protection-toggle]');
    if (!toggle) return;

    const desiredEnabled = toggle.checked;

    setCheckoutDisabled(true);

    // Apply optimistic UI INSTANTLY
    applyOptimisticUI(desiredEnabled);

    // Update the disabled flag
    setDisabled(!desiredEnabled);

    // Store the latest desired state
    pendingDesiredState = desiredEnabled;

    // Mark that the user is actively toggling (blocks all syncs)
    isUserToggling = true;

    // Reset the debounce timer — only fires after 300ms of quiet
    clearTimeout(toggleDebounceTimer);
    toggleDebounceTimer = setTimeout(() => {
      drainToggleQueue();
    }, 300);
  };

  /**
   * Starts processing the toggle queue, but only if not already running.
   * If already running, the active loop will pick up the new
   * pendingDesiredState automatically — no duplicate work needed.
   */
  const drainToggleQueue = async () => {
    if (isProcessingQueue) return; // already running — it will pick up the new state
    isProcessingQueue = true;

    try {
      while (pendingDesiredState !== null) {
        const desired = pendingDesiredState;
        pendingDesiredState = null; // consume it

        await executeToggle(desired);

        // If another click came in while we were processing, loop again
        if (pendingDesiredState !== null) {
          continue;
        }
        break;
      }
    } finally {
      isProcessingQueue = false;
      isUserToggling = false;
      setCheckoutDisabled(false);
    }
  };

  /**
   * Executes a single add/remove operation and reconciles with server.
   */
  const executeToggle = async (desiredEnabled) => {
    try {
      const cart = await getCart();
      resolveProtectionPrice(cart);
      const protectionItems = getProtectionItems(cart);
      const isCurrentlyInCart = protectionItems.length > 0;

      let finalCart;

      if (desiredEnabled && !isCurrentlyInCart) {
        // Need to add protection
        const response = await postCart('/cart/add.js', {
          id: variantIdString,
          quantity: 1
        });

        if (!protectionPriceCents && response) {
          protectionPriceCents = response.final_price || response.price || 0;
        }

        finalCart = await getCart();

      } else if (!desiredEnabled && isCurrentlyInCart) {
        // Need to remove protection
        await postCart('/cart/change.js', {
          id: protectionItems[0].key || variantIdString,
          quantity: 0
        });
        finalCart = await getCart();

      } else {
        // Cart already matches desired state
        finalCart = cart;
      }

      // If another click came in while we were processing, do NOT update the UI.
      // Let the next loop iteration handle the UI update to avoid reverting the optimistic UI.
      if (pendingDesiredState !== null) {
        return;
      }

      // Reconcile everything from server
      if (finalCart && typeof finalCart.total_price !== 'undefined') {
        updateSubtotalFromServer(finalCart.total_price);
      }
      updateCartCounters(finalCart);
      refreshDonationBar(finalCart);
      optimisticState = desiredEnabled;

      // Enforce correct widget state
      setWidgetState(desiredEnabled);
      setIconAppearance(desiredEnabled);

    } catch (error) {
      console.error('[ShippingProtection] Toggle failed', error);
      // Revert optimistic UI on failure
      const revertState = !desiredEnabled;
      applyOptimisticUI(revertState);
      setDisabled(desiredEnabled);
    }
  };

  const handleDisableClick = (event) => {
    const button = event.target.closest('[data-shipping-protection-disable]');
    if (!button) return;

    event.preventDefault();

    // Route through the same toggle path
    applyOptimisticUI(false);
    setDisabled(true);

    pendingDesiredState = false;
    isUserToggling = true;

    clearTimeout(toggleDebounceTimer);
    toggleDebounceTimer = setTimeout(() => {
      drainToggleQueue();
    }, 300);
  };

  // ── Bare add / remove for sync (no optimistic UI) ──────────────────
  const addProtectionToCart = async () => {
    if (!variantIdString) return;
    setDisabled(false);

    try {
      const response = await postCart('/cart/add.js', {
        id: variantIdString,
        quantity: 1
      });

      if (!protectionPriceCents && response) {
        protectionPriceCents = response.final_price || response.price || 0;
      }

      // Reconcile price
      const finalCart = await getCart();
      if (finalCart && typeof finalCart.total_price !== 'undefined') {
        updateSubtotalFromServer(finalCart.total_price);
      }
      updateCartCounters(finalCart);
      refreshDonationBar(finalCart);
      setWidgetState(true);
      setIconAppearance(true);
      optimisticState = true;
    } catch (error) {
      console.error('[ShippingProtection] Add failed', error);
    }
  };

  const removeProtectionFromCart = async (lineKey) => {
    if (!lineKey) return;

    try {
      await postCart('/cart/change.js', {
        id: lineKey,
        quantity: 0
      });

      const finalCart = await getCart();
      if (finalCart && typeof finalCart.total_price !== 'undefined') {
        updateSubtotalFromServer(finalCart.total_price);
      }
      updateCartCounters(finalCart);
      refreshDonationBar(finalCart);
      setWidgetState(false);
      setIconAppearance(false);
      optimisticState = false;
    } catch (error) {
      console.error('[ShippingProtection] Remove failed', error);
    }
  };

  const enforceSingleProtection = async (protectionItems) => {
    if (!protectionItems.length) return;

    const [keep, ...extras] = protectionItems;
    const tasks = [];

    if (keep.quantity > 1) {
      tasks.push(postCart('/cart/change.js', { id: keep.key, quantity: 1 }));
    }

    extras.forEach(item => {
      tasks.push(postCart('/cart/change.js', { id: item.key, quantity: 0 }));
    });

    if (tasks.length) {
      await Promise.all(tasks);
      const finalCart = await getCart();
      if (finalCart && typeof finalCart.total_price !== 'undefined') {
        updateSubtotalFromServer(finalCart.total_price);
      }
      updateCartCounters(finalCart);
      refreshDonationBar(finalCart);
    }
  };

  const resetDisabledStateIfCartEmpty = async (cart) => {
    const regularItems = getRegularItems(cart);
    if (regularItems.length > 0) return;

    const protectionItems = getProtectionItems(cart);
    setDisabled(false);
    updateSubtotalFromServer(0);
    setWidgetState(true);
    setIconAppearance(true);
    optimisticState = true;
    pendingDesiredState = null;
    isUserToggling = false;
    isProcessingQueue = false;
    clearTimeout(toggleDebounceTimer);
    setCheckoutDisabled(false);

    if (protectionItems.length) {
      const protectionKey = protectionItems[0].key || variantIdString;
      const cartItems = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
      if (cartItems && typeof cartItems.updateQuantity === 'function') {
        // Use cart.js to remove it so it automatically fetches and renders the Empty Cart UI
        cartItems.updateQuantity(protectionKey, 0);
      } else {
        await removeProtectionFromCart(protectionKey);
      }
    }
  };

  // ── Sync logic ─────────────────────────────────────────────────────
  // COMPLETELY blocked while user is actively toggling (from first
  // click through final API settlement). This prevents sync from
  // undoing the user's toggle.
  const syncShippingProtection = async () => {
    if (isSyncing || !variantIdString || isUserToggling) return;

    // Instantly enforce the visual state based on sessionStorage (prevents fake "ON" for disabled users)
    const currentlyDisabled = isDisabled();
    setWidgetState(!currentlyDisabled);
    setIconAppearance(!currentlyDisabled);
    optimisticState = !currentlyDisabled;

    isSyncing = true;

    try {
      const cart = await getCart();
      resolveProtectionPrice(cart);
      updateCartCounters(cart);

      const protectionItems = getProtectionItems(cart);
      const regularItems = getRegularItems(cart);

      if (regularItems.length === 0) {
        await resetDisabledStateIfCartEmpty(cart);
        return;
      }

      if (isDisabled() && protectionItems.length) {
        await removeProtectionFromCart(protectionItems[0].key);
        return;
      }

      if (protectionItems.length > 1 || (protectionItems[0] && protectionItems[0].quantity > 1)) {
        await enforceSingleProtection(protectionItems);
        return;
      }

      if (!protectionItems.length && !isDisabled()) {
        await addProtectionToCart();
      }
    } catch (error) {
      console.error('[ShippingProtection] Sync failed', error);
    } finally {
      isSyncing = false;
    }
  };

  const isCheckoutForm = (form) => {
    if (!form || !form.matches('form')) return false;
    if (form.id === 'CartDrawer-FormSummary' || form.id === 'cart') return true;
    return !!form.querySelector('[name="checkout"]');
  };

  const ensureProtectionForCheckout = async () => {
    if (!variantIdString) return;

    setCheckoutDisabled(true);

    if (pendingDesiredState !== null || isUserToggling || isProcessingQueue) {
      await drainToggleQueue();
    }

    const desiredEnabled = !isDisabled();
    if (desiredEnabled) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }

    const cart = await getCart();
    const protectionItems = getProtectionItems(cart);

    if (desiredEnabled) {
      if (!protectionItems.length) {
        await addProtectionToCart();
      }
    } else if (protectionItems.length) {
      await removeProtectionFromCart(protectionItems[0].key || variantIdString);
    }
  };

  // ── Event listeners ────────────────────────────────────────────────
  document.addEventListener('change', handleToggle);
  document.addEventListener('click', handleDisableClick);
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!isCheckoutForm(form)) return;
    if (form.dataset.spCheckoutReady === 'true') return;

    event.preventDefault();

    try {
      form.dataset.spCheckoutReady = 'true';
      await ensureProtectionForCheckout();
      form.submit();
    } catch (error) {
      form.dataset.spCheckoutReady = '';
      setCheckoutDisabled(false);
      console.error('[ShippingProtection] Checkout sync failed', error);
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    resolveProtectionPrice(null);
    initOptimisticState();
    syncShippingProtection();
  });

  [
    'cart:updated',
    'cart:refresh',
    'cart:change',
    'product:added',
    'ajaxProduct:added',
    'cart-drawer:refresh',
    'theme:cart:change'
  ].forEach(eventName => {
    document.addEventListener(eventName, syncShippingProtection);
  });

  // ── DOM insertion observer ─────────────────────────────────────────
  // The cart drawer re-renders its HTML via AJAX after every add/remove.
  // The widget is always injected with `checked` hardcoded in the Liquid
  // template, so we watch for it appearing in the DOM and immediately
  // apply the correct state from sessionStorage — no timing dependency.
  const domObserver = new MutationObserver(() => {
    const toggle = document.querySelector('[data-shipping-protection-toggle]');
    if (!toggle) return;

    const shouldBeEnabled = !isDisabled();

    // Only correct if the rendered HTML disagrees with the real state
    if (toggle.checked !== shouldBeEnabled) {
      setWidgetState(shouldBeEnabled);
    }
    setIconAppearance(shouldBeEnabled);
    optimisticState = shouldBeEnabled;
  });

  domObserver.observe(document.body, { childList: true, subtree: true });

  window.QamarShippingProtection = {
    sync: syncShippingProtection,
    disable: () => {
      applyOptimisticUI(false);
      setDisabled(true);
      pendingDesiredState = false;
      isUserToggling = true;
      clearTimeout(toggleDebounceTimer);
      drainToggleQueue();
    },
    add: () => {
      applyOptimisticUI(true);
      setDisabled(false);
      pendingDesiredState = true;
      isUserToggling = true;
      clearTimeout(toggleDebounceTimer);
      drainToggleQueue();
    },
    remove: removeProtectionFromCart,
    getCart
  };
})();
